import { Injectable } from "@angular/core";
import { FormArray, FormBuilder, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { AppAbility } from "../../util/service/auth.service";
import { Observable } from "rxjs";
import { EntityTyp } from "../../util/enums/entity-typ.enum";
import { BaseFormGroupService } from "../../util/components/modal/base-form-group.service";
import { OfficeNameDto, ReportDto, ReportEntryDto } from "app/api/models";
import { ReportService } from "app/api/services";
import { CaslAction } from "app/server/enums/casl-action.enum";
import { DateTime } from "luxon";
import { ReportEntryCategory } from "../../server/enums/report-entry-category.enum";

@Injectable({
  providedIn: "root",
})
export class ReportFormGroupService extends BaseFormGroupService<ReportDto> {
  entitySingular = "Bericht";

  offices!: OfficeNameDto[];

  constructor(
    private fb: FormBuilder,
    public override modal: NgbActiveModal,
    protected toastr: ToastrService,
    private reportService: ReportService,
    private ability: AppAbility
  ) {
    super(modal, toastr);
  }

  get reportEntries() {
    return this.formGroup.get("reportEntries") as FormArray;
  }

  get officeIdFormControl() {
    return this.formGroup.get("officeId");
  }

  get housingIdFormControl() {
    return this.formGroup.get("housingId");
  }

  get officeId() {
    return this.officeIdFormControl.value;
  }

  retrieveOffice(officeId: number) {
    return this.offices?.find(({ id }) => id === officeId);
  }

  get currentOffice() {
    const officeId = this.officeId;
    return this.retrieveOffice(officeId);
  }

  initFormGroup() {
    this.formGroup = this.fb.group({
      housingId: [null],
      subject: [null],
      mainEmployeeId: [null, Validators.required],
      officeId: [null, Validators.required],
      otherEmployees: [[]],
      reportEntries: this.fb.array([]),
      version: [0, Validators.required],
      type: [null],
    });
    this.setPermissions();
  }

  addReportEntry(data?: Partial<ReportEntryDto>, isNewReport = false) {
    const newEntry = this.fb.group({
      id: [null],
      affectedClients: [[]],
      category: [ReportEntryCategory.Standard, Validators.required],
      details: ["", Validators.required],
      isIncident: [false, Validators.required],
      isBlueLightIncident: [false, Validators.required],
      version: [0, Validators.required],
    });
    newEntry.get("isIncident").valueChanges.subscribe((value) => {
      if (!value) {
        newEntry.get("isBlueLightIncident").setValue(false);
      }
    });
    if (data) {
      newEntry.patchValue(data as any);
    }
    this.reportEntries.push(newEntry);
    if (!isNewReport) {
      this.formGroup.markAsDirty();
    }
  }

  removeReportEntry(i: number) {
    this.reportEntries.removeAt(i);
    this.formGroup.markAsDirty();
  }

  protected create(mergedEntity: ReportDto): Observable<ReportDto> {
    return this.reportService.reportControllerCreate({ body: mergedEntity });
  }

  protected update(mergedEntity: ReportDto): Observable<ReportDto> {
    return this.reportService.reportControllerUpdate({
      body: mergedEntity,
    });
  }

  protected reloadEntity(id: number): Observable<ReportDto> {
    return this.reportService.reportControllerFindOne({ id });
  }

  protected updateFormGroup(entity: ReportDto) {
    this.formGroup.patchValue(entity, { emitEvent: false });
    this.reportEntries.clear();
    entity.reportEntries.forEach((entry) => this.addReportEntry(entry));
    this.setPermissions(entity);
  }

  private setPermissions(entity?: ReportDto) {
    if (
      this.ability.can(CaslAction.Update, EntityTyp.Report) &&
      (!entity || DateTime.fromISO(entity.createdDate) >= DateTime.now().minus({ week: 1 }))
    ) {
      return;
    }
    this.formGroup.disable({ emitEvent: false });
  }
}
